import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  FileText,
  ImagePlus,
  Pencil,
  Plus,
  Target,
  Trash2,
} from "lucide-react";
import type { CoachingNote } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { invalidateThumbnail } from "@/features/coach-review/hooks/useThumbnail";
import { VideoNoteThumbnail } from "@/features/coach-review/components/VideoNoteThumbnail";
import { TacticalBoardThumbnail } from "@/features/canvas/TacticalBoardThumbnail";
import { useMatches } from "@/features/matches/useMatches";
import {
  useCoachNoteMutations,
  useCoachNotesList,
} from "./hooks/useCoachNotesList";
import { PageHeader } from "./components/PageHeader";
import {
  BulkSelectionBar,
  SelectToggle,
} from "./components/BulkSelectionBar";
import type { GoalComposerSource } from "./components/GoalComposerModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import type { CoachingVisibility } from "@/lib/types";
import {
  playerAudienceLabel,
  visibilityLabel,
} from "./feedbackLibraryLabels";

/**
 * Coach > Notes list page (M6a).
 *
 * Ports the legacy `renderCoachNotes` (`js/coaching/notes.js`):
 * the full team-notes list (video + observation rows), each with
 * a thumbnail tile, a context pill, a meta line, and the per-row
 * action set.
 *
 * Creation routes INTO Coach Review (the M4→M6 carry-in — the
 * note/observation composer lives there, built in M4):
 *   - **+ New note** → `/coach/review?source=video`
 *   - **+ New observation** → `/coach/review?source=tactical_board`
 *
 * Per-row actions:
 *   - **Edit** → `/coach/review?note_id=…` — Coach Review picks the
 *     note vs. observation composer from `note_context` and restores
 *     the saved match / timestamp / drawing context.
 *   - **Regenerate thumbnail** (video notes only) — best-effort
 *     server regen + a `useThumbnail` cache invalidation so the
 *     refreshed JPEG shows.
 *   - **Delete** — confirm + `deleteCoachingNote`.
 *
 * Video-note thumbnails render the raw frame plus a lightweight SVG
 * overlay from the note drawing so saved telestration marks are
 * visible from the library list.
 *
 * Observation rows show event metadata (type / title / date)
 * instead of match/timestamp/slot — the meta line never renders
 * a stale "0:00" / "Full" for fields that don't apply to an
 * observation. Observations also suppress "Regenerate thumbnail"
 * (no video frame to seek / thumbnail).
 */

const SLOT_LABELS: Record<string, string> = {
  full: "Full match",
  first_half: "1st half",
  second_half: "2nd half",
};

export type NotesPageProps = {
  /** M7.5 D1: opens the goal composer with the note as source
   * evidence. Undefined ⇒ the row's "+ New goal" button is
   * hidden (no dead button if the shell hasn't wired it). */
  onCreatePlayerGoal?: (
    playerId: string,
    source: GoalComposerSource,
  ) => void;
  mode?: "all" | "notes" | "observations";
};

export function NotesPage({
  onCreatePlayerGoal,
  mode = "all",
}: NotesPageProps = {}) {
  const navigate = useNavigate();
  const toast = useToast();
  const notesQuery = useCoachNotesList();
  const matchesQuery = useMatches();
  const mutations = useCoachNoteMutations();

  // Resolve `match_id` → a display label for video-note meta lines.
  const matchLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of matchesQuery.data ?? []) {
      const teams = `${m.home_team} vs ${m.away_team}`;
      map.set(m.id, m.date ? `${teams} · ${m.date}` : teams);
    }
    return map;
  }, [matchesQuery.data]);

  const notes = notesQuery.data?.notes ?? [];
  const visibleNotes = useMemo(() => {
    if (mode === "notes") {
      return notes.filter((note) => note.note_context !== "observation");
    }
    if (mode === "observations") {
      return notes.filter((note) => note.note_context === "observation");
    }
    return notes;
  }, [mode, notes]);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CoachingNote | null>(null);
  const pageCopy = notesPageCopy(mode);

  // A6: bulk selection. `selected` holds note ids; the bar appears
  // once ≥1 is selected. `confirmBulkDelete` opens the danger dialog
  // for the whole batch.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelected = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelected(new Set());

  const selectedIds = useMemo(
    () => visibleNotes.filter((n) => selected.has(n.id)).map((n) => n.id),
    [visibleNotes, selected],
  );
  // A6: how many selected rows have no tagged players — those won't
  // reach a player even with "player" visibility (a player-visibility
  // note with zero tags is invisible to viewers).
  const selectedUntaggedCount = useMemo(
    () =>
      visibleNotes.filter(
        (n) => selected.has(n.id) && (n.player_ids?.length ?? 0) === 0,
      ).length,
    [visibleNotes, selected],
  );

  const handleDelete = (note: CoachingNote) => {
    setConfirmDelete(note);
  };

  const confirmDeleteNote = async () => {
    if (!confirmDelete) return;
    const note = confirmDelete;
    setConfirmDelete(null);
    setDeleting(note.id);
    try {
      await mutations.deleteNote(note.id);
      toast.success("Note deleted");
    } catch (err) {
      toast.error("Couldn't delete note", { description: errorMessage(err) });
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkVisibility = async (visibility: CoachingVisibility) => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const { ok, failed } = await mutations.bulkUpdateVisibility(
        selectedIds,
        visibility,
      );
      if (failed === 0) {
        toast.success(`Updated visibility on ${ok} note${ok === 1 ? "" : "s"}`);
      } else {
        toast.error(
          `Updated ${ok}, failed ${failed} — check the rows that didn't change.`,
        );
      }
      clearSelection();
    } catch (err) {
      toast.error("Couldn't update visibility", {
        description: errorMessage(err),
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmBulkDeleteNotes = async () => {
    setConfirmBulkDelete(false);
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const { ok, failed } = await mutations.bulkDelete(selectedIds);
      if (failed === 0) {
        toast.success(`Deleted ${ok} note${ok === 1 ? "" : "s"}`);
      } else {
        toast.error(`Deleted ${ok}, failed ${failed}.`);
      }
      clearSelection();
    } catch (err) {
      toast.error("Couldn't delete notes", { description: errorMessage(err) });
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="notes-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete note?"
          message={`Delete "${confirmDelete.title?.trim() || confirmDelete.event_title?.trim() || "this note"}"? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeleteNote}
          onClose={() => setConfirmDelete(null)}
          data-testid="notes-delete-confirm"
        />
      ) : null}
      {confirmBulkDelete ? (
        <ConfirmDialog
          title="Delete selected notes?"
          message={`Delete ${selectedIds.length} note${selectedIds.length === 1 ? "" : "s"}? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmBulkDeleteNotes}
          onClose={() => setConfirmBulkDelete(false)}
          data-testid="notes-bulk-delete-confirm"
        />
      ) : null}
      <PageHeader
        title={pageCopy.title}
        description={pageCopy.description}
        actions={
          <>
            {mode !== "observations" ? (
              <Button
                type="button"
                onClick={() =>
                  navigate({ to: "/coach/review", search: { source: "video" } })
                }
                data-testid="notes-new-note"
                aria-label="New note"
                title="New note"
                size="sm"
                className="h-8 w-8 px-0"
              >
                <Plus size={16} strokeWidth={2} aria-hidden="true" />
              </Button>
            ) : null}
            {mode !== "notes" ? (
              <Button
                type="button"
                onClick={() =>
                  navigate({
                    to: "/coach/review",
                    search: { source: "tactical_board" },
                  })
                }
                data-testid="notes-new-observation"
                variant={mode === "observations" ? "primary" : "outline"}
                aria-label="New observation"
                title="New observation"
                size="sm"
                className="h-8 w-8 px-0"
              >
                <FileText size={16} strokeWidth={2} aria-hidden="true" />
              </Button>
            ) : null}
          </>
        }
      />

      {notesQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="notes-loading"
        >
          Loading notes…
        </p>
      ) : visibleNotes.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="notes-empty"
        >
          {pageCopy.empty}
        </p>
      ) : (
        <>
          <BulkSelectionBar
            count={selectedIds.length}
            busy={bulkBusy}
            untaggedWarning={
              selectedUntaggedCount > 0
                ? `${selectedUntaggedCount} selected ${selectedUntaggedCount === 1 ? "note has" : "notes have"} no tagged players — "Player + family" won't reach a player for ${selectedUntaggedCount === 1 ? "it" : "them"}.`
                : null
            }
            onSetVisibility={handleBulkVisibility}
            onDelete={() => setConfirmBulkDelete(true)}
            onClear={clearSelection}
            testIdPrefix="notes"
          />
          <ul className="flex flex-col gap-2" data-testid="notes-list">
            {visibleNotes.map((note) => (
              <NoteRow
                key={note.id}
                note={note}
                selected={selected.has(note.id)}
                onToggleSelected={() => toggleSelected(note.id)}
              matchLabel={
                note.match_id
                  ? (matchLabelById.get(note.match_id) ?? note.match_id)
                  : ""
              }
              deleting={deleting === note.id}
              onOpenInReview={() =>
                navigate({
                  to: "/coach/review",
                  search: {
                    note_id: note.id,
                    source:
                      note.note_context === "observation"
                        ? "tactical_board"
                        : "video",
                  },
                })
              }
              onRegenerate={async () => {
                try {
                  await mutations.regenerateThumbnail(note.id);
                  // Drop the cached blob so the refreshed JPEG shows.
                  invalidateThumbnail("note", note.id);
                  toast.success("Thumbnail regenerated");
                } catch (err) {
                  toast.error("Couldn't regenerate thumbnail", {
                    description: errorMessage(err),
                  });
                }
              }}
              onDelete={() => handleDelete(note)}
              // M7.5 D1: per-row "+ New goal" — gated like legacy
              // on `note.player_ids?.length` (a goal needs a target
              // player). The first tagged player becomes the
              // preselect; the modal lets the coach change it.
              onCreateGoal={
                onCreatePlayerGoal && note.player_ids?.length
                  ? () =>
                      onCreatePlayerGoal(note.player_ids![0], {
                        source_note_id: note.id,
                        seedTitle: note.title?.trim() || undefined,
                        seedDescription:
                          note.what_to_do_next?.trim() || undefined,
                      })
                  : undefined
              }
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function notesPageCopy(mode: NotesPageProps["mode"]): {
  title: string;
  description: string;
  empty: string;
} {
  if (mode === "notes") {
    return {
      title: "Notes",
      description:
        "Player-facing teaching points and coach-only working notes. Each row shows audience, visibility, source, and next action.",
      empty: "No coaching notes yet. Use + New note to add the first one.",
    };
  }
  if (mode === "observations") {
    return {
      title: "Observations",
      description:
        "Tactical-board and non-video observations that can become player feedback, goals, or recap evidence.",
      empty:
        "No observations yet. Use + New observation to add the first one.",
    };
  }
  return {
    title: "Feedback Notes",
    description:
      "Player-facing teaching points, observations, and coach-only working notes. Each row shows audience, visibility, source, and next action.",
    empty:
      "No coaching notes yet. Use + New note or + New observation to add the first one.",
  };
}

type NoteRowProps = {
  note: CoachingNote;
  matchLabel: string;
  deleting: boolean;
  selected: boolean;
  onToggleSelected: () => void;
  onOpenInReview: () => void;
  onRegenerate: () => void;
  onDelete: () => void;
  /** M7.5 D1: undefined ⇒ no goal button (the gate is upstream:
   * `note.player_ids?.length > 0` AND the shell wired the
   * callback). */
  onCreateGoal?: () => void;
};

function NoteRow({
  note,
  matchLabel,
  deleting,
  selected,
  onToggleSelected,
  onOpenInReview,
  onRegenerate,
  onDelete,
  onCreateGoal,
}: NoteRowProps) {
  const isObservation = note.note_context === "observation";
  const title =
    note.title?.trim() ||
    (isObservation
      ? note.event_title?.trim() || "Observation note"
      : "(untitled)");

  // Meta line — observation rows show event metadata, video rows
  // show match/timestamp/slot. Never a stale "0:00 / Full" for an
  // observation.
  const metaParts: string[] = [
    playerAudienceLabel(note.player_ids),
    `Player sees: ${visibilityLabel(note.visibility)}`,
  ];
  if (isObservation) {
    if (note.event_type) {
      const t = note.event_type;
      metaParts.push(`Source: ${t[0].toUpperCase()}${t.slice(1)} observation`);
    } else {
      metaParts.push("Source: Observation");
    }
    if (note.event_title && note.event_title !== title) {
      metaParts.push(note.event_title);
    }
    if (note.event_date) metaParts.push(note.event_date);
  } else {
    if (matchLabel) metaParts.push(`Source: ${matchLabel}`);
    metaParts.push(formatPlayhead(note.timestamp_seconds));
    if (note.slot) metaParts.push(SLOT_LABELS[note.slot] ?? note.slot);
  }
  if (note.category) metaParts.push(note.category);
  metaParts.push("Next: Review or goal");

  return (
    <li
      className="grid gap-2 rounded border border-rule bg-surface p-2 shadow-sm sm:grid-cols-[auto_4rem_minmax(0,1fr)_auto] sm:items-center"
      data-testid="note-row"
      data-note-id={note.id}
      data-note-context={note.note_context}
      data-selected={selected ? "true" : "false"}
    >
      <SelectToggle
        selected={selected}
        onToggle={onToggleSelected}
        label={selected ? "Deselect note" : "Select note"}
        testid="note-select-toggle"
      />
      <NoteThumb note={note} isObservation={isObservation} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className="rounded px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wider bg-surface text-ink-muted"
            data-context-pill={note.note_context}
          >
            {isObservation ? "Observation" : "Video"}
          </span>
          <span className="font-display text-ink truncate">{title}</span>
        </div>
        <div className="text-ink-muted text-xs font-mono mt-0.5 truncate">
          {metaParts.join(" · ")}
        </div>
        {note.body?.trim() ? (
          <div className="text-ink-muted text-xs mt-0.5 truncate">
            {note.body.trim()}
          </div>
        ) : null}
      </div>
      <div className="inline-flex w-max shrink-0 flex-nowrap items-center gap-0.5 rounded border border-rule bg-elevated p-0.5 sm:justify-self-end">
        <RowButton
          label="Edit"
          testid="note-action-edit"
          onClick={onOpenInReview}
          icon={<Pencil size={16} strokeWidth={2} aria-hidden="true" />}
        />
        {!isObservation ? (
          <RowButton
            label="Regenerate thumbnail"
            testid="note-action-regenerate"
            onClick={onRegenerate}
            icon={<ImagePlus size={16} strokeWidth={2} aria-hidden="true" />}
          />
        ) : null}
        {/* M7.5 D1: per-row "+ New goal" — only when the note is
         * tagged to at least one player (gating handled upstream
         * by the callback presence). */}
        {onCreateGoal ? (
          <RowButton
            label="+ New goal"
            testid="note-action-goal"
            onClick={onCreateGoal}
            icon={<Target size={16} strokeWidth={2} aria-hidden="true" />}
          />
        ) : null}
        <RowButton
          label="Delete"
          testid="note-action-delete"
          onClick={onDelete}
          disabled={deleting}
          danger
          icon={<Trash2 size={16} strokeWidth={2} aria-hidden="true" />}
        />
      </div>
    </li>
  );
}

/** Note thumbnail tile. Video notes fetch the access-controlled
 * JPEG via `useThumbnail`; observations render a compact tactical
 * board preview from `tactical_board_json` when present, or a field
 * thumbnail fallback so the row still has a meaningful visual cue. */
function NoteThumb({
  note,
  isObservation,
}: {
  note: CoachingNote;
  isObservation: boolean;
}) {
  if (!isObservation) {
    return (
      <VideoNoteThumbnail
        note={note}
        testId="note-thumb"
        placeholderTestId="note-thumb-placeholder"
        overlayTestId="note-thumb-drawing-overlay"
        className="relative block h-11 w-16 shrink-0 overflow-hidden rounded border border-rule/60 bg-gradient-to-br from-canvas to-surface"
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className="relative block h-11 w-16 shrink-0 overflow-hidden rounded border border-rule/60 bg-gradient-to-br from-canvas to-surface"
      data-testid="note-thumb-observation"
    >
      <TacticalBoardThumbnail
        raw={note.tactical_board_json}
        width={64}
        fallback="field"
        testId="note-thumb-observation-board"
        className="absolute inset-0 flex items-center justify-center bg-surface"
      />
    </span>
  );
}

function RowButton(props: {
  label: string;
  testid: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.label}
      aria-label={props.label}
      className={[
        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:bg-destructive/10 hover:text-destructive"
          : "border-rule text-ink-muted hover:border-accent hover:bg-accent/10 hover:text-accent",
      ].join(" ")}
    >
      {props.icon}
    </button>
  );
}
